import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { 
  Loader2, 
  Phone, 
  MessageSquare, 
  CheckCircle2, 
  AlertCircle, 
  RefreshCw,
  PhoneCall,
  Copy
} from "lucide-react";
import { cn } from "@/lib/utils";
import twilioService, { TwilioSmsMessage, TwilioVerificationCodeResponse } from '../../services/twilioService';

interface WhatsAppVerificationProps {
  twilioPurchasedNumber: string;
  numberType?: 'local' | 'mobile';
  onCodeReceived?: (code: string) => void;
  /**
   * Scope every lookup to this line. Onboarding leaves it unset because the
   * account has exactly one number then; connecting a further WhatsApp line
   * must set it, otherwise another number's code would show up here.
   */
  scopeNumber?: string;
  /**
   * Let the user switch between call and SMS regardless of `numberType`.
   * Meta offers both for a number it considers mobile-capable, and for a
   * further agent line we cannot know upfront which one the user picks.
   */
  allowMethodSwitch?: boolean;
  /**
   * Set the verification webhook on mount. Off when the parent already routed
   * the line into verification mode before opening the Meta popup.
   */
  autoConfigureWebhook?: boolean;
  /** Headline + intro; the connect flow words this differently. */
  title?: string;
  description?: string;
}

type VerificationMethod = 'sms' | 'voice';

export const WhatsAppVerification: React.FC<WhatsAppVerificationProps> = ({
  twilioPurchasedNumber,
  numberType = 'local',
  onCodeReceived,
  scopeNumber,
  allowMethodSwitch,
  autoConfigureWebhook = true,
  title = 'WhatsApp Verifizierung',
  description,
}) => {
  const showMethodSwitch = allowMethodSwitch ?? numberType === 'mobile';
  const [verificationMethod, setVerificationMethod] = useState<VerificationMethod>(
    numberType === 'local' ? 'voice' : 'sms'
  );
  const [smsMessages, setSmsMessages] = useState<TwilioSmsMessage[]>([]);
  const [isLoadingSms, setIsLoadingSms] = useState(false);
  const [verificationCode, setVerificationCode] = useState<string>('');
  const [codeReceivedAt, setCodeReceivedAt] = useState<string>('');
  const [recordingUrl, setRecordingUrl] = useState<string>('');
  const [rawTranscript, setRawTranscript] = useState<string>('');
  const [isPollingCode, setIsPollingCode] = useState(false);
  const [voiceStatus, setVoiceStatus] = useState<TwilioVerificationCodeResponse | null>(null);
  const [status, setStatus] = useState<{ type: 'success' | 'error' | 'info', message: string } | null>(null);
  const [copyFeedback, setCopyFeedback] = useState<'ai' | 'manual' | null>(null);
  const [isConfiguringWebhook, setIsConfiguringWebhook] = useState(false);
  const [webhookConfigured, setWebhookConfigured] = useState(false);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const pollCountRef = useRef(0);
  const isPollingActiveRef = useRef(false);
  const webhookConfigAttemptedRef = useRef(false);
  const lastCodeRef = useRef<string>('');
  const lastReceivedAtRef = useRef<string>('');

  // Auto-configure verification webhook on mount (for existing numbers)
  useEffect(() => {
    if (webhookConfigAttemptedRef.current) return;
    if (!autoConfigureWebhook) return;
    if (numberType !== 'local') return;

    webhookConfigAttemptedRef.current = true;

    const configureWebhook = async () => {
      try {
        setIsConfiguringWebhook(true);
        setStatus({ type: 'info', message: 'Webhook wird konfiguriert...' });

        const response = await twilioService.configureVerificationWebhook(scopeNumber);

        if (response.success) {
          setWebhookConfigured(true);
          setStatus({ type: 'info', message: 'Bereit für Verifizierungsanruf.' });
          console.log('Verification webhook configured:', response.voice_webhook_url);
        }
      } catch (error: any) {
        console.error('Error configuring webhook:', error);
        // Don't show error to user - webhook might already be configured
        setStatus(null);
      } finally {
        setIsConfiguringWebhook(false);
      }
    };
    
    configureWebhook();
  }, [numberType, autoConfigureWebhook, scopeNumber]);

  // Load SMS messages for mobile numbers
  const loadSmsMessages = useCallback(async () => {
    if (!twilioPurchasedNumber || verificationMethod !== 'sms') return;
    try {
      setIsLoadingSms(true);
      const response = await twilioService.getOnboardingSms(5, scopeNumber);
      setSmsMessages(response.messages || []);
      
      // Try to extract code from latest SMS
      if (response.messages?.length) {
        const latestSms = response.messages[0];
        const codeMatch = latestSms.body?.match(/\b(\d{6})\b/);
        if (codeMatch) {
          setVerificationCode(codeMatch[1]);
          onCodeReceived?.(codeMatch[1]);
        }
      }
    } catch (error) {
      console.error('Error loading SMS:', error);
    } finally {
      setIsLoadingSms(false);
    }
  }, [twilioPurchasedNumber, verificationMethod, onCodeReceived, scopeNumber]);

  // Poll for voice verification code — keep polling even after a code is received,
  // so that a newer code from a subsequent verification call replaces the old one.
  const pollForCode = useCallback(async () => {
    if (!isPollingActiveRef.current) return;

    try {
      // No phone_number_id — the backend matches by the called Twilio number,
      // scoped to this line when the account has more than one.
      const response = await twilioService.getVerificationCode(undefined, scopeNumber);
      setVoiceStatus(response);

      // Expose recording + transcript even before the code is extracted, so
      // the user can listen as soon as the audio finishes uploading.
      if (response.recording_url) {
        setRecordingUrl(response.recording_url);
      }
      if (response.raw_transcript) {
        setRawTranscript(response.raw_transcript);
      }

      if (response.code) {
        // Only update state + notify parent if this is a NEW code
        // (detected via received_at timestamp, falling back to the code value itself)
        const receivedAt = response.received_at || '';
        const isNewCode =
          response.code !== lastCodeRef.current ||
          (receivedAt && receivedAt !== lastReceivedAtRef.current);

        if (isNewCode) {
          lastCodeRef.current = response.code;
          lastReceivedAtRef.current = receivedAt;
          setVerificationCode(response.code);
          setCodeReceivedAt(receivedAt);
          setStatus({ type: 'success', message: `Code empfangen! Bitte mit der Aufnahme abgleichen.` });
          onCodeReceived?.(response.code);
        }
      } else if (response.expired) {
        // Don't show error - just keep polling, user might retry on Facebook
        console.log('Previous verification expired, continuing to poll...');
      }

      // Stop polling after 300 attempts (10 minutes) - enough for the full Facebook flow
      pollCountRef.current++;
      if (pollCountRef.current >= 300) {
        setStatus({ type: 'info', message: 'Zeitüberschreitung. Klicken Sie auf "Erneut prüfen" um weiter zu suchen.' });
        stopPolling();
      }
    } catch (error) {
      console.error('Error polling for code:', error);
    }
  }, [onCodeReceived, scopeNumber]);

  const startPolling = useCallback(() => {
    if (isPollingActiveRef.current) return; // Don't start if already polling
    
    pollCountRef.current = 0;
    isPollingActiveRef.current = true;
    setIsPollingCode(true);
    
    // Poll immediately, then every 2 seconds
    pollForCode();
    pollingIntervalRef.current = setInterval(pollForCode, 2000);
  }, [pollForCode]);

  const stopPolling = () => {
    isPollingActiveRef.current = false;
    setIsPollingCode(false);
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };

  // Copy code to clipboard
  const handleCopyCode = (source: 'ai' | 'manual', value: string) => {
    if (!value) return;
    navigator.clipboard.writeText(value);
    setCopyFeedback(source);
    onCodeReceived?.(value);
    setTimeout(() => setCopyFeedback(null), 2000);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, []);

  // Auto-start polling for voice verification when component mounts.
  // Keep polling even after a code has been received so that a newer code
  // from a subsequent verification call can replace the displayed one.
  useEffect(() => {
    if (verificationMethod === 'voice' && twilioPurchasedNumber) {
      startPolling();
    }
    return () => {
      if (verificationMethod === 'voice') {
        stopPolling();
      }
    };
  }, [verificationMethod, twilioPurchasedNumber, startPolling]);

  // Auto-load SMS for mobile numbers
  useEffect(() => {
    if (verificationMethod === 'sms' && twilioPurchasedNumber) {
      loadSmsMessages();
      const interval = setInterval(loadSmsMessages, 10000);
      return () => clearInterval(interval);
    }
  }, [verificationMethod, twilioPurchasedNumber, loadSmsMessages]);

  // Switch to voice polling when method changes
  useEffect(() => {
    if (verificationMethod === 'voice') {
      startPolling();
    } else if (verificationMethod === 'sms') {
      stopPolling();
    }
  }, [verificationMethod, startPolling]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">{title}</CardTitle>
        <CardDescription>
          {description ??
            (numberType === 'local'
              ? 'Ihre Festnetznummer wird per Anruf verifiziert'
              : 'Wählen Sie Ihre bevorzugte Verifizierungsmethode')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Method Selection — a mobile line (or an unknown one) can go either way */}
        {showMethodSwitch && (
          <div className="flex gap-2">
            <Button
              type="button"
              variant={verificationMethod === 'sms' ? 'default' : 'outline'}
              onClick={() => setVerificationMethod('sms')}
              className={cn(
                "flex-1",
                verificationMethod === 'sms' && "bg-[#1967D2] hover:bg-[#1558b0]"
              )}
            >
              <MessageSquare className="h-4 w-4 mr-2" />
              SMS
            </Button>
            <Button
              type="button"
              variant={verificationMethod === 'voice' ? 'default' : 'outline'}
              onClick={() => setVerificationMethod('voice')}
              className={cn(
                "flex-1",
                verificationMethod === 'voice' && "bg-[#1967D2] hover:bg-[#1558b0]"
              )}
            >
              <Phone className="h-4 w-4 mr-2" />
              Anruf
            </Button>
          </div>
        )}

        {/* SMS Verification UI */}
        {verificationMethod === 'sms' && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                <MessageSquare className="h-4 w-4 text-[#1967D2]" />
                Eingehende SMS
              </div>
              <Button 
                type="button" 
                variant="ghost" 
                size="sm"
                onClick={loadSmsMessages} 
                disabled={isLoadingSms}
              >
                <RefreshCw className={cn("h-4 w-4", isLoadingSms && "animate-spin")} />
              </Button>
            </div>
            
            {smsMessages.length === 0 && (
              <p className="text-sm text-muted-foreground bg-muted p-3 rounded-md">
                Noch keine SMS erhalten. Sobald Meta die Verifizierung sendet, erscheint sie hier.
              </p>
            )}

            {smsMessages.map((sms) => (
              <div key={sms.sms_sid || sms.received_at} className="border rounded-md p-3 text-sm bg-card">
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>{sms.from_number || 'Unbekannt'}</span>
                  <span>{sms.received_at ? new Date(sms.received_at).toLocaleString() : ''}</span>
                </div>
                <p className="text-muted-foreground mt-1">{sms.body || '-'}</p>
              </div>
            ))}
          </div>
        )}

        {/* Voice Verification UI - SIMPLIFIED */}
        {verificationMethod === 'voice' && (
          <div className="space-y-4">
            <div className="bg-primary/10 border border-primary/25 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <PhoneCall className="h-5 w-5 text-primary-strong mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-primary-strong">So funktioniert's:</p>
                  <ol className="text-sm text-primary-strong mt-1 space-y-1 list-decimal list-inside">
                    <li>Klicken Sie auf <strong>"Mit Facebook verbinden"</strong></li>
                    <li>Geben Sie Ihre Nummer ein: <strong className="font-mono">{twilioPurchasedNumber}</strong></li>
                    <li>Wählen Sie <strong>"Anruf"</strong> als Verifizierungsmethode</li>
                    <li>Der Code erscheint hier <strong>automatisch</strong></li>
                  </ol>
                </div>
              </div>
            </div>

            {/* Polling Status - show only when nothing received yet */}
            {isPollingCode && !verificationCode && !recordingUrl && (
              <div className="flex items-center justify-center gap-3 py-4 bg-muted rounded-lg">
                <Loader2 className="h-5 w-5 animate-spin text-primary-strong" />
                <span className="text-sm text-muted-foreground">
                  Warte auf Verifizierungsanruf von Meta...
                </span>
              </div>
            )}

            {/* Audio Recording Playback — primary source of truth */}
            {recordingUrl && (
              <div className="bg-muted border border-border rounded-lg p-4 space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                  <PhoneCall className="h-4 w-4 text-primary-strong" />
                  Aufnahme des Verifizierungsanrufs
                </div>
                <audio
                  controls
                  src={recordingUrl}
                  className="w-full"
                  preload="metadata"
                />
                <p className="text-xs text-muted-foreground">
                  Hören Sie die Aufnahme an und notieren Sie sich den gesprochenen Code.
                  Der automatisch erkannte Code unten ist nur ein Vorschlag – bitte abgleichen.
                </p>
              </div>
            )}

            {/* AI-extracted code (tentative, verify against recording) */}
            {verificationCode && (
              <div className="bg-[#E8F0FB] border-2 border-[#1967D2] rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle2 className="h-5 w-5 text-[#1967D2]" />
                  <span className="text-sm font-medium text-[#1558b0]">
                    Automatisch erkannter Code
                  </span>
                </div>
                <div className="flex items-center justify-center gap-3">
                  <span className="font-mono text-4xl font-bold tracking-[0.5em] text-[#1558b0]">
                    {verificationCode}
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleCopyCode('ai', verificationCode)}
                    className="border-[#1967D2] hover:bg-[#D4E3F8]"
                  >
                    {copyFeedback === 'ai' ? (
                      <CheckCircle2 className="h-4 w-4 text-[#1967D2]" />
                    ) : (
                      <Copy className="h-4 w-4 text-[#1967D2]" />
                    )}
                  </Button>
                </div>
                <p className="text-xs text-[#1558b0] mt-3">
                  Bitte mit der Aufnahme abgleichen und bei Facebook eingeben.
                </p>
                {rawTranscript && (
                  <details className="mt-2 text-xs text-[#1558b0]/80">
                    <summary className="cursor-pointer">Rohes Transkript anzeigen</summary>
                    <p className="mt-1 font-mono whitespace-pre-wrap">{rawTranscript}</p>
                  </details>
                )}
              </div>
            )}
          </div>
        )}


        {/* Status Message */}
        {status && (
          <Alert className={cn(
            status.type === 'success' ? "bg-success/10 border-success/25 text-success-strong" :
            status.type === 'error' ? "bg-destructive/10 border-destructive/30 text-destructive-strong" :
            "bg-primary/10 border-primary/25 text-primary-strong"
          )}>
            {status.type === 'success' ? <CheckCircle2 className="h-4 w-4" /> : 
             status.type === 'error' ? <AlertCircle className="h-4 w-4" /> :
             <Loader2 className="h-4 w-4 animate-spin" />}
            <AlertDescription>{status.message}</AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

export default WhatsAppVerification;
